import type { Flag, QualityBlock, QualityReport } from '@/lib/contract';
import { QUALITY_BLOCKS } from '@/lib/contract';
import { cn } from '@/lib/cn';
import { Panel } from '@/ui/panel';
import { Badge } from '@/ui/badge';
import { MicroLabel } from '@/ui/micro-label';
import { Tooltip } from '@/ui/tooltip';

/**
 * The verdict, in full: five blocks in fixed order, their qualifiers, and the flag history.
 *
 * Shared by the operator console and the vendor portal deliberately. Both are reading one run's
 * conclusion, and a vendor shown a differently-worded version of the verdict a decision was made
 * on has been told something else -- so there is one component, and neither realm can drift.
 */

const VERDICT_SWATCH: Record<string, string> = {
  pass: 'bg-pass',
  warn: 'bg-warn',
  fail: 'bg-danger',
  not_applicable: 'bg-inset',
  unevaluated: 'bg-inset'
};

const VERDICT_TEXT: Record<string, 'pass' | 'warn' | 'danger' | 'dim'> = {
  pass: 'pass',
  warn: 'warn',
  fail: 'danger',
  not_applicable: 'dim',
  unevaluated: 'dim'
};

/**
 * Written out rather than interpolated. Tailwind generates classes it can find as literal strings in
 * the source, so a `text-${tone}` built at runtime compiles to a class that does not exist and the
 * text silently renders in the inherited colour.
 */
const VERDICT_CLASS: Record<string, string> = {
  pass: 'text-pass',
  warn: 'text-warn',
  fail: 'text-danger',
  not_applicable: 'text-dim',
  unevaluated: 'text-dim'
};

/** Findings, worst first: an operator hovering a failed block is looking for the failure. */
const SEVERITY: Record<string, number> = { fail: 0, warn: 1, not_applicable: 2, unevaluated: 3, pass: 4 };

function CheckList({ checks }: { checks: NonNullable<QualityBlock['checks']> }) {
  return (
    <ul className="grid max-w-[420px] gap-1.5">
      {[...checks]
        .sort((a, b) => (SEVERITY[a.verdict] ?? 9) - (SEVERITY[b.verdict] ?? 9))
        .map((check, i) => (
          <li key={`${check.code}-${i}`} className="grid grid-cols-[38px_1fr] gap-2">
            <span className={cn('font-mono text-[10px] uppercase', VERDICT_CLASS[check.verdict] ?? 'text-dim')}>
              {check.verdict}
            </span>
            <span className="text-[11px] leading-snug text-foreground">
              <span className="font-mono text-[10px] text-muted">{check.code}</span>
              {check.observed ? <span className="block text-muted">{check.observed}</span> : null}
            </span>
          </li>
        ))}
    </ul>
  );
}

export function QualityReportPanel({ report, flags }: { report: QualityReport; flags: Flag[] }) {
  return (
    <Panel
      title={`Quality report · ${report.version_label}`}
      subtitle={report.meta}
      action={
        report.gate ? (
          <span className="rounded-[3px] border border-border px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-pass uppercase">
            Gate: {report.gate}
          </span>
        ) : null
      }
    >
      <ul className="grid">
        {report.blocks.map((block, index) => {
          const definition = QUALITY_BLOCKS[index];
          const qualifier = report.qualifiers[block.key];
          const row = (
            <li
              key={block.key}
              className={cn(
                'grid grid-cols-[16px_20px_1fr_124px_1fr] items-center gap-3 py-2.5',
                index > 0 && 'border-t border-border',
                // Only a block with findings behind it is worth reaching for.
                block.checks?.length && 'cursor-help'
              )}
              tabIndex={block.checks?.length ? 0 : undefined}
            >
              <span className={cn('block h-3 w-4 rounded-[2px]', VERDICT_SWATCH[block.verdict])} />
              <span className="font-mono text-[11px] text-dim">{index + 1}</span>
              <MicroLabel size="xs" tone="muted">
                {definition.label}
              </MicroLabel>
              <MicroLabel size="xs" tone={VERDICT_TEXT[block.verdict]} className="whitespace-nowrap">
                {block.verdict === 'not_applicable' ? 'N/A' : block.verdict}
                {qualifier ? ` · ${qualifier}` : ''}
              </MicroLabel>
              <span className="font-mono text-[11px] text-muted">{block.detail}</span>
            </li>
          );
          /**
           * The summary says "1 fail". The tooltip says *which*, and what was observed.
           *
           * Without it the only way to learn that a gate failed on ACTION_STATE_PAIRED rather than
           * on anything else was to query the database -- the API has shipped these findings all
           * along, and every screen threw them away. Hovering is the cheapest place to put an answer
           * an operator needs before they can approve or reject.
           */
          return block.checks?.length ? (
            <Tooltip
              key={block.key}
              label={<CheckList checks={block.checks} />}
              className="normal-case tracking-normal"
            >
              {row}
            </Tooltip>
          ) : (
            row
          );
        })}
      </ul>

      <MicroLabel size="xs" className="mt-5 block">
        Flag history · episode references
      </MicroLabel>
      {flags.length ? (
        <ul className="mt-2 grid">
          {flags.map(flag => (
            <li key={flag.code} className="grid grid-cols-[60px_80px_1fr] items-center gap-3 py-2">
              <span className="font-mono text-[11px] text-foreground">{flag.code}</span>
              <Badge tone={flag.state === 'open' ? (flag.blocking ? 'danger' : 'warn') : 'pass'}>{flag.state}</Badge>
              <span className="font-mono text-[11px] text-muted">
                {flag.summary} · {flag.episode_refs.join(', ')}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 font-mono text-[11px] text-dim">No flags raised.</p>
      )}
    </Panel>
  );
}
